import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ConfiguratorDatastoreService, DatastoreDto} from '@tj/tj-dmp-lib-api';
import {filter, map} from 'rxjs/operators';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationService, MenuItem, MessageService} from "primeng/api";
import {ClassicTreeSave} from "../../../components/filter/filter-preset/filter-preset.component";

@Component({
    selector: 'dmp-datastore',
    templateUrl: './datastore.component.html',
    styleUrls: ['./datastore.component.scss'],
    providers: [MessageService],
    standalone: false
})
export class DatastoreComponent implements OnInit {

    constructor(
        protected datastoreService: ConfiguratorDatastoreService,
        private confirmationService: ConfirmationService,
        private formBuilder: UntypedFormBuilder,
        private messageService: MessageService,
        private router: Router,
        private readonly translate: TranslateService
    ) {
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }
    breadcrumb = [
        {label: this.translate.instant('menu.data-center.label')},
        {label: this.translate.instant('menu.data-center.datastores')}
    ];
    description = this.translate.instant('datastore.description');

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.data-center.label')},
            {label: this.translate.instant('menu.data-center.datastores')}
        ];
        this.description = this.translate.instant('datastore.description');
    }
    datastores: DatastoreDto[];
    currentDatastore: string;

    // Création d'un groupe, en modale depuis la liste
    newDatastoreVisible = false;
    isSavingNewDatastore = false;
    newDatastoreForm: UntypedFormGroup = this.formBuilder.group({
        name: ['', [Validators.required, Validators.minLength(3)]],
        code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(5)]],
        description: ['']
    });

    public readonly actionList: any[] = [
        {
            label: this.translate.instant('subscriptions.edit'),
            icon: 'fas fa-pencil-alt',
            permission: 'DATASTORES.edit',
            command: () => {
                this.action('edit', this.currentDatastore);
            },
        },
        {
            label: this.translate.instant('subscriptions.delete'),
            icon: 'fas fa-trash-alt',
            permission: 'DATASTORES.delete',
            command: () => {
                this.action('delete', this.currentDatastore);
            },
        }
    ];

    ngOnInit(): void {
        this.loadAll();
    }

    loadAll() {
        this.datastoreService
            .getAllDatastoresUsingGET('response')
            .pipe(
                filter((res: HttpResponse<DatastoreDto[]>) => res.ok),
                map((res: HttpResponse<DatastoreDto[]>) => res.body)
            )
            .subscribe(
                (res: DatastoreDto[]) => {
                    this.datastores = res;
                },
                (res: HttpErrorResponse) => {
                    /** */
                }
            );
    }

    get newName(): AbstractControl {
        return this.newDatastoreForm.get('name');
    }

    get newCode(): AbstractControl {
        return this.newDatastoreForm.get('code');
    }

    /**
     * Le formulaire est remis à zéro à l'ouverture plutôt qu'à la fermeture : la croix et
     * « Annuler » n'ont alors qu'à masquer la modale, et une saisie abandonnée ne peut pas
     * réapparaître à l'ouverture suivante.
     */
    openNewDatastore(): void {
        this.newDatastoreForm.reset({name: '', code: '', description: ''});
        this.newDatastoreVisible = true;
    }

    saveNewDatastore(): void {
        this.isSavingNewDatastore = true;
        const {name, code, description} = this.newDatastoreForm.value;
        const datastoreCode = 'T_' + String(code).toUpperCase();
        this.datastoreService.createDatastoreUsingPOST(datastoreCode, {name, code: datastoreCode, description})
            .subscribe(
                () => {
                    this.isSavingNewDatastore = false;
                    this.newDatastoreVisible = false;
                    this.loadAll();
                },
                () => {
                    this.isSavingNewDatastore = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('datastore.createGroupFailed'),
                        life: 5000
                    });
                }
            );
    }

    action(name: 'view' | 'edit' | 'delete', id: string) {
        switch (name) {
            case 'view':
                this.router.navigate(['/h/settings/datastore', id, 'view']);
                break;
            case 'edit':
                this.router.navigate(['/h/settings/datastore', id, 'edit']);
                break;
            case 'delete':
                this.confirmationService.confirm({
                    message: 'Voulez-vous le supprimer ?',
                    accept: () => {
                        const datastore = this.datastores.find(d => d.id === id);
                        this.datastoreService.deleteDatastoreUsingDELETE(datastore.code, id).subscribe(() => {
                            this.loadAll();
                        });
                    }
                });
                break;
        }
    }
}
